// Record a scheduled service visit as completed on its REAL past date (Session AEB).
//
// The Director's case, verbatim: a service genuinely carried out on site but never entered -
// the scheduled visit still sits in the system as upcoming, and nothing offered a way to
// complete it retrospectively. This modal is that way. It deliberately has the SAME rigour as
// a live engineer completion, never less:
//
//   - the real past visit date (never defaulted to today; the scheduled date is the starting
//     point, clamped to today) and who did the work (Engineer register picker, editable);
//   - the SAME 14-area checklist recording UI the engineer PWA and the office service panel
//     use (src/components/AreaChecklist.tsx + the ServiceRecordPanel office glue - reused,
//     never a second checklist component), with the same honesty guards: nothing records as
//     a Pass until its area is deliberately confirmed, and a Fail requires a note;
//   - NO signature step of any kind. A backdated office entry cannot fabricate a customer
//     signature; the server stamps the record "office_entered_retrospectively" and the
//     certificate renders no signature block (the migrated paper-era precedent).
//
// Saving does NOT complete the job: it submits it for the ordinary office approval
// (pending_approval), and approving from Job sheets generates the certificate, files it to
// Drive and emails the customer - the same gate as a live sheet. The success view says so
// plainly, and offers the pre-approval undo (the two-step revert).
import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { BackdatedEntryBody, JobSheetChecklistEntry, ScheduleEngineer } from "../../lib/api";
import { buttonClasses, ButtonLink } from "../../components/Button";
import { formatDateLong } from "../../lib/format";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { AreaChecklist } from "../../components/AreaChecklist";
import { ConfirmButton } from "../../components/ConfirmButton";
import { JobPhotoUploader } from "./JobPhotoUploader";
import type { ChecklistItemDef, ChecklistStatus } from "../../lib/offline/types";
import { buildAreaGroups, confirmableAreas, unconfirmedAreaCount, areaFirstFailMissingNote } from "../../lib/serviceAreas";
import { checklistUsesAreaEditor, officeAreaItems, deriveOfficeChecklist, type OfficeAreaSourceItem } from "./ServiceRecordPanel";

// A local YYYY-MM-DD for a date input.
function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// The default visit date: the visit's scheduled date when it is in the past (the usual case -
// the plan said the 9th and the work happened around then), else today. Always editable.
export function defaultVisitYmd(scheduledForIso: string | null, now: Date): string {
  if (scheduledForIso) {
    const s = new Date(scheduledForIso);
    if (!Number.isNaN(s.getTime()) && s.getTime() <= now.getTime()) return toYmd(s);
  }
  return toYmd(now);
}

// The flat editor's status options (the non-area templates: equipment / goods / legacy).
const FLAT_STATUS_OPTIONS = [
  { value: "na", label: "N/A" },
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "advisory", label: "Advisory" },
] as const;

type FlatRow = { id: string; item: string; category?: string; status: string; note: string };

// Validate + assemble the save body from the editor state. Pure, exported for tests. Returns
// either the body or a plain-English error naming exactly what is missing.
export function buildBackdatedBody(state: {
  visitDateYmd: string;
  engineerId: string;
  areaMode: boolean;
  areaSource: OfficeAreaSourceItem[];
  areaItems: ChecklistItemDef[];
  areaStatuses: Record<string, ChecklistStatus>;
  areaConfirmed: Record<string, boolean>;
  areaNotes: Record<string, string>;
  flatRows: FlatRow[];
  defects: string[];
  engineerNotes: string;
  now: Date;
}): { ok: true; body: BackdatedEntryBody } | { ok: false; message: string } {
  if (!state.visitDateYmd) return { ok: false, message: "Enter the real date the service was carried out." };
  const todayYmd = toYmd(state.now);
  if (state.visitDateYmd > todayYmd) {
    return { ok: false, message: "The visit date cannot be in the future. Pick the date the work actually happened." };
  }
  const noon = new Date(`${state.visitDateYmd}T12:00:00`);
  if (Number.isNaN(noon.getTime())) return { ok: false, message: "Enter the real date the service was carried out." };
  // A past day is stamped at local noon. TODAY before noon is stamped at `now` instead - the
  // server rightly refuses any future INSTANT, and a visit done this morning entered at 10:00
  // used to 400 until midday (review finding).
  const visit = state.visitDateYmd === todayYmd && noon.getTime() > state.now.getTime() ? state.now : noon;

  let checklist: JobSheetChecklistEntry[];
  if (state.areaMode) {
    const areas = buildAreaGroups(state.areaItems, []);
    for (const area of confirmableAreas(areas)) {
      const missing = areaFirstFailMissingNote(area, state.areaStatuses, state.areaNotes);
      if (missing) return { ok: false, message: `"${missing.description}" is marked Fail: add a note saying what was found.` };
    }
    const unconfirmed = unconfirmedAreaCount(areas, state.areaConfirmed);
    if (unconfirmed > 0) {
      return { ok: false, message: `Confirm every inspection area before saving (${unconfirmed} to go). A backdated record carries the same detail as a live one.` };
    }
    checklist = deriveOfficeChecklist(state.areaSource, areas, state.areaStatuses, state.areaConfirmed, state.areaNotes);
  } else {
    const recorded = state.flatRows.filter((r) => r.status !== "na");
    if (recorded.length === 0) {
      return { ok: false, message: "Record the checks that were carried out: set at least one item (everything left N/A claims nothing)." };
    }
    const failNoNote = state.flatRows.find((r) => r.status === "fail" && !r.note.trim());
    if (failNoNote) return { ok: false, message: `"${failNoNote.item}" is marked Fail: add a note saying what was found.` };
    checklist = state.flatRows.map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      item: r.item,
      status: r.status,
      ...(r.category ? { category: r.category } : {}),
      ...(r.note.trim() ? { note: r.note.trim() } : {}),
    }));
  }

  const defects = state.defects.map((d) => d.trim()).filter(Boolean);
  return {
    ok: true,
    body: {
      visitDate: visit.toISOString(),
      ...(state.engineerId ? { engineerId: state.engineerId } : {}),
      checklist,
      ...(defects.length ? { defects } : {}),
      ...(state.engineerNotes.trim() ? { engineerNotes: state.engineerNotes.trim() } : {}),
    },
  };
}

// The modal serves two doors into the same record (Session AES added the second):
//   jobId   -> the visit is already on the schedule and was never entered (Session AEB);
//   assetId -> the visit was never on the schedule at all, so it is created with its record.
// Everything between those two points is identical: the same checklist, the same rigour, the
// same no-signature rule, the same approval gate afterwards. Exactly one must be given.
export function BackdatedCompletionModal({
  jobId,
  assetId,
  liftLabel,
  scheduledForIso,
  onClose,
  onRecorded,
}: {
  jobId?: string;
  assetId?: string;
  liftLabel: string; // e.g. "ELE-078 | Rosina Street" - the header context line
  scheduledForIso: string | null;
  onClose: () => void;
  // Called after the record is saved (and again after an undo), so the host refreshes its list.
  onRecorded: () => void;
}) {
  // In asset mode the job does not exist until the save returns, so the undo needs the id the
  // server allocated. In job mode it is the prop, unchanged.
  const [savedJobId, setSavedJobId] = useState<string | null>(jobId ?? null);
  const [phase, setPhase] = useState<"loading" | "form" | "saving" | "done" | "blocked">("loading");
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [templateName, setTemplateName] = useState<string | null>(null);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [engineerId, setEngineerId] = useState("");
  const [visitDateYmd, setVisitDateYmd] = useState(() => defaultVisitYmd(scheduledForIso, new Date()));

  // Area editor state (the passenger ELE-MS-001 path) - the exact ServiceRecordPanel shapes.
  const [areaMode, setAreaMode] = useState(false);
  const [areaSource, setAreaSource] = useState<OfficeAreaSourceItem[]>([]);
  const [areaStatuses, setAreaStatuses] = useState<Record<string, ChecklistStatus>>({});
  const [areaConfirmed, setAreaConfirmed] = useState<Record<string, boolean>>({});
  const [areaNotes, setAreaNotes] = useState<Record<string, string>>({});
  // Flat editor state (equipment / goods / legacy templates).
  const [flatRows, setFlatRows] = useState<FlatRow[]>([]);

  const [defects, setDefects] = useState<string[]>([]);
  const [engineerNotes, setEngineerNotes] = useState("");
  const [reverting, setReverting] = useState(false);

  const areaItems = useMemo(() => officeAreaItems(areaSource), [areaSource]);
  const areas = useMemo(() => buildAreaGroups(areaItems, []), [areaItems]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [template, engineerRows] = await Promise.all([
          jobId ? api.jobsheets.checklistTemplate(jobId) : api.jobsheets.assetChecklistTemplate(assetId!),
          api.scheduling.engineers(),
        ]);
        if (cancelled) return;
        setTemplateName(template.name);
        setEngineers(engineerRows);
        // One active engineer -> preselect (the usual single-engineer reality); more -> the
        // office picks deliberately, so a visit is never silently mis-attributed.
        if (engineerRows.length === 1) setEngineerId(engineerRows[0]!.id);
        if (checklistUsesAreaEditor(template.items)) {
          setAreaMode(true);
          setAreaSource(template.items.map((i) => ({ id: i.id, item: i.item, ...(i.category ? { category: i.category } : {}) })));
        } else {
          setAreaMode(false);
          setFlatRows(template.items.map((i) => ({ id: i.id, item: i.item, ...(i.category ? { category: i.category } : {}), status: "na", note: "" })));
        }
        setPhase("form");
      } catch (err) {
        if (cancelled) return;
        setBlockedMessage(
          err instanceof ApiError && (err.status === 404 || err.status === 409)
            ? err.message || "No standard checklist applies to this visit, so it cannot be recorded through this flow."
            : "Could not load the checklist for this visit. Please try again.",
        );
        setPhase("blocked");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, assetId]);

  function setAreaItemStatus(id: string, status: ChecklistStatus | null) {
    setAreaStatuses((s) => {
      const c = { ...s };
      if (status == null) delete c[id];
      else c[id] = status;
      return c;
    });
    if (status == null || status === "na") {
      setAreaNotes((n) => {
        if (!n[id]) return n;
        const c = { ...n };
        delete c[id];
        return c;
      });
    }
    if (error) setError(null);
  }

  async function save() {
    const built = buildBackdatedBody({
      visitDateYmd,
      engineerId,
      areaMode,
      areaSource,
      areaItems,
      areaStatuses,
      areaConfirmed,
      areaNotes,
      flatRows,
      defects,
      engineerNotes,
      now: new Date(),
    });
    if (!built.ok) {
      setError(built.message);
      return;
    }
    setPhase("saving");
    setError(null);
    try {
      if (jobId) {
        await api.jobsheets.recordBackdated(jobId, built.body);
      } else {
        // Asset mode: the visit is created with its record. Keep the id the server allocated so
        // the undo below can reach it (there is no job to fall back on).
        const created = await api.jobsheets.recordRetrospectiveVisit({ ...built.body, assetId: assetId! });
        setSavedJobId(created.jobId);
      }
      setPhase("done");
      onRecorded();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not save the record. Please try again.");
      setPhase("form");
    }
  }

  async function undo() {
    if (!savedJobId) {
      setError("This record cannot be undone automatically. Find it on Service records.");
      return;
    }
    setReverting(true);
    setError(null);
    try {
      await api.jobsheets.revertBackdated(savedJobId);
      onRecorded();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not undo the entry. Check it on Service records.");
      setReverting(false);
    }
  }

  const busy = phase === "saving" || reverting;
  const dateDisplay = visitDateYmd ? formatDateLong(`${visitDateYmd}T12:00:00`) : "";

  return (
    <ModalShell ariaLabel="Record a visit as completed (backdated)" onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[85vh] overflow-y-auto">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">{jobId ? "Record as completed" : "Record a past visit"}</h2>
          <p className="text-sm text-ele-body">{liftLabel}</p>
        </div>
        <button onClick={() => !busy && onClose()} aria-label="Close" className="text-ele-body hover:text-ele-green">
          ✕
        </button>
      </div>

      {phase === "loading" && <p className="text-sm text-ele-body">Loading the checklist...</p>}

      {phase === "blocked" && (
        <>
          <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{blockedMessage}</p>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>Close</button>
          </div>
        </>
      )}

      {phase === "done" && (
        <>
          <p className="rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            Recorded: this visit is now a service record dated {dateDisplay}, awaiting release on Service records.
          </p>
          <p className="mt-3 text-sm text-ele-body">
            Releasing it generates the service certificate and files it to Drive, exactly as a live visit. Nothing goes to the
            customer until you press Send email now. The record is marked internally as entered by the office after the visit; that never appears on the
            customer document.
          </p>
          {savedJobId && (
            <div className="mt-4">
              <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-blue">Add photos (optional)</h3>
              <p className="mb-1 text-xs text-ele-body opacity-70">
                Attach any photos of the visit to this record now, or add them later from the record. A record that carries photos cannot be undone
                automatically: delete its photos first.
              </p>
              <JobPhotoUploader jobId={savedJobId} label="" />
            </div>
          )}
          {error && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}
          <div className={`mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
            <ConfirmButton
              onConfirm={undo}
              label="Undo this entry"
              confirmLabel="Press again to undo"
              busy={reverting}
              busyLabel="Undoing..."
              className={buttonClasses("destructive", "md", MODAL_ACTION_BTN)}
            />
            <ButtonLink to="/office/jobsheets" variant="secondary" className={MODAL_ACTION_BTN}>
              Open Service records to release
            </ButtonLink>
            <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>Close</button>
          </div>
        </>
      )}

      {(phase === "form" || phase === "saving") && (
        <>
          <p className="mb-4 text-sm text-ele-body">
            For a service that was genuinely carried out on site but never entered. Fill in the same detail the engineer
            would have recorded that day; the record then goes through the normal approval before anything reaches the
            customer. No customer signature is recorded on a backdated entry.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Date the work was done</span>
              <input
                type="date"
                value={visitDateYmd}
                max={toYmd(new Date())}
                onChange={(e) => {
                  setVisitDateYmd(e.target.value);
                  if (error) setError(null);
                }}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Who did the work</span>
              <select
                value={engineerId}
                onChange={(e) => setEngineerId(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
              >
                <option value="">Not on the engineer list</option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4">
            <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">
              {templateName ? `${templateName} checklist` : "Checklist"}
            </h3>
            {areaMode ? (
              <>
                <p className="mb-2 text-xs text-ele-body opacity-70">
                  A clean visit is one action: confirm all areas checked and passed. Found something that day? Open the area
                  and record it against the exact check.
                </p>
                <AreaChecklist
                  areas={areas}
                  confirmed={areaConfirmed}
                  statuses={areaStatuses}
                  notes={areaNotes}
                  onConfirmedChange={(next) => {
                    setAreaConfirmed(next);
                    if (error) setError(null);
                  }}
                  onStatusChange={setAreaItemStatus}
                  onNoteChange={(id, v) => setAreaNotes((n) => ({ ...n, [id]: v }))}
                />
              </>
            ) : (
              <>
                <p className="mb-2 text-xs text-ele-body opacity-70">
                  Set each check that was carried out; leave anything not done as N/A (an honest non-claim).
                </p>
                <ul className="space-y-2">
                  {flatRows.map((r, i) => (
                    <li key={r.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 text-sm text-ele-strong">{r.item}</span>
                        <select
                          aria-label={`Result for ${r.item}`}
                          value={r.status}
                          onChange={(e) => {
                            const status = e.target.value;
                            setFlatRows((rows) => rows.map((row, j) => (j === i ? { ...row, status } : row)));
                            if (error) setError(null);
                          }}
                          className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
                        >
                          {FLAT_STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        value={r.note}
                        onChange={(e) => {
                          const note = e.target.value;
                          setFlatRows((rows) => rows.map((row, j) => (j === i ? { ...row, note } : row)));
                        }}
                        placeholder="Note (optional; required for a Fail)"
                        aria-label={`Note for ${r.item}`}
                        className="mt-2 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">Defects found (optional)</h3>
              <button
                type="button"
                onClick={() => setDefects((d) => [...d, ""])}
                className="font-menu text-xs tracking-wide text-ele-green hover:underline"
              >
                Add defect
              </button>
            </div>
            {defects.length > 0 && (
              <ul className="space-y-2">
                {defects.map((d, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <input
                      value={d}
                      onChange={(e) => setDefects((rows) => rows.map((row, j) => (j === i ? e.target.value : row)))}
                      placeholder="What was found"
                      aria-label={`Defect ${i + 1}`}
                      className="min-w-0 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                    <button
                      type="button"
                      onClick={() => setDefects((rows) => rows.filter((_, j) => j !== i))}
                      aria-label={`Remove defect ${i + 1}`}
                      className="text-ele-body hover:text-ele-danger"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Notes (optional)</span>
            <textarea
              value={engineerNotes}
              onChange={(e) => setEngineerNotes(e.target.value)}
              rows={2}
              placeholder="Anything worth recording about the visit"
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
            />
          </label>

          {error && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

          <div className={`mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
            {/* Two-step confirm (the locked standard for a permanent-record action): the first
                press arms, the second saves. Green - constructive - with the arming escalation. */}
            <ConfirmButton
              onConfirm={save}
              label="Record as completed"
              confirmLabel={`Press again: ${dateDisplay || "this date"}`}
              busy={phase === "saving"}
              busyLabel="Saving..."
              armedClassName="!bg-ele-green !text-ele-bg font-semibold"
              className={buttonClasses("secondary", "md", MODAL_ACTION_BTN)}
            />
            <button onClick={() => !busy && onClose()} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
              Cancel
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}
